import { type Address } from "viem";

// ─── PoolKey struct ──────────────────────────────────────────────────────────
export interface PoolKey {
  currency0: Address;   // lower address
  currency1: Address;   // higher address
  hooks: Address;       // 0x0000...0000 for no hooks
  poolManager: Address; // CL_POOL_MANAGER or BIN_POOL_MANAGER
  fee: number;          // e.g., 2500 = 0.25%
  parameters: `0x${string}`; // encodes tickSpacing(CL) or binStep(BIN)
}

// ─── Quoter ABI ──────────────────────────────────────────────────────────────
export const QUOTER_ABI = [
  {
    type: "function",
    name: "quoteExactInputSingle",
    inputs: [
      {
        name: "params",
        type: "tuple",
        components: [
          {
            name: "poolKey",
            type: "tuple",
            components: [
              { name: "currency0", type: "address" },
              { name: "currency1", type: "address" },
              { name: "hooks", type: "address" },
              { name: "poolManager", type: "address" },
              { name: "fee", type: "uint24" },
              { name: "parameters", type: "bytes32" },
            ],
          },
          { name: "zeroForOne", type: "bool" },
          { name: "exactAmount", type: "uint128" },
          { name: "hookData", type: "bytes" },
        ],
      },
    ],
    outputs: [
      { name: "amountOut", type: "uint256" },
      { name: "gasEstimate", type: "uint256" },
    ],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "quoteExactInput",
    inputs: [
      { name: "path", type: "bytes" },
      { name: "amountIn", type: "uint256" },
    ],
    outputs: [
      { name: "amountOut", type: "uint256" },
      { name: "gasEstimate", type: "uint256" },
    ],
    stateMutability: "nonpayable",
  },
] as const;

// ─── SmartRouter (V4) ABI ────────────────────────────────────────────────────
export const SMART_ROUTER_ABI = [
  {
    type: "function",
    name: "exactInputSingle",
    inputs: [
      {
        name: "params",
        type: "tuple",
        components: [
          {
            name: "poolKey",
            type: "tuple",
            components: [
              { name: "currency0", type: "address" },
              { name: "currency1", type: "address" },
              { name: "hooks", type: "address" },
              { name: "poolManager", type: "address" },
              { name: "fee", type: "uint24" },
              { name: "parameters", type: "bytes32" },
            ],
          },
          { name: "zeroForOne", type: "bool" },
          { name: "recipient", type: "address" },
          { name: "amountIn", type: "uint128" },
          { name: "amountOutMinimum", type: "uint128" },
          { name: "hookData", type: "bytes" },
        ],
      },
    ],
    outputs: [{ name: "amountOut", type: "uint256" }],
    stateMutability: "payable",
  },
  {
    type: "function",
    name: "exactInput",
    inputs: [
      {
        name: "params",
        type: "tuple",
        components: [
          { name: "path", type: "bytes" },
          { name: "recipient", type: "address" },
          { name: "amountIn", type: "uint256" },
          { name: "amountOutMinimum", type: "uint256" },
        ],
      },
    ],
    outputs: [{ name: "amountOut", type: "uint256" }],
    stateMutability: "payable",
  },
] as const;

// ─── CL Position Manager ABI ─────────────────────────────────────────────────
export const CL_POSITION_MANAGER_ABI = [
  {
    type: "function",
    name: "modifyLiquidities",
    inputs: [
      { name: "unlockData", type: "bytes" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [],
    stateMutability: "payable",
  },
  {
    type: "function",
    name: "getPosition",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [
      { name: "poolId", type: "bytes32" },
      { name: "tickLower", type: "int24" },
      { name: "tickUpper", type: "int24" },
      { name: "liquidity", type: "uint128" },
      { name: "feeGrowthInside0LastX128", type: "uint256" },
      { name: "feeGrowthInside1LastX128", type: "uint256" },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "balanceOf",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "tokenOfOwnerByIndex",
    inputs: [
      { name: "owner", type: "address" },
      { name: "index", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "approve",
    inputs: [
      { name: "spender", type: "address" },
      { name: "tokenId", type: "uint256" },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "multicall",
    inputs: [{ name: "data", type: "bytes[]" }],
    outputs: [{ name: "results", type: "bytes[]" }],
    stateMutability: "payable",
  },
] as const;

// ─── BIN Position Manager ABI ────────────────────────────────────────────────
export const BIN_POSITION_MANAGER_ABI = [
  {
    type: "function",
    name: "modifyLiquidities",
    inputs: [
      { name: "unlockData", type: "bytes" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [],
    stateMutability: "payable",
  },
  {
    type: "function",
    name: "balanceOf",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "tokenOfOwnerByIndex",
    inputs: [
      { name: "owner", type: "address" },
      { name: "index", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "multicall",
    inputs: [{ name: "data", type: "bytes[]" }],
    outputs: [{ name: "results", type: "bytes[]" }],
    stateMutability: "payable",
  },
] as const;

// ─── Permit2 ABI ─────────────────────────────────────────────────────────────
export const PERMIT2_ABI = [
  {
    type: "function",
    name: "allowance",
    inputs: [
      { name: "owner", type: "address" },
      { name: "token", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [
      { name: "amount", type: "uint160" },
      { name: "expiration", type: "uint48" },
      { name: "nonce", type: "uint48" },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "approve",
    inputs: [
      { name: "token", type: "address" },
      { name: "spender", type: "address" },
      { name: "amount", type: "uint160" },
      { name: "expiration", type: "uint48" },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
] as const;

// ─── CL Pool Manager ABI (for reading pool state) ────────────────────────────
export const CL_POOL_MANAGER_ABI = [
  {
    type: "function",
    name: "getSlot0",
    inputs: [{ name: "id", type: "bytes32" }],
    outputs: [
      { name: "sqrtPriceX96", type: "uint160" },
      { name: "tick", type: "int24" },
      { name: "protocolFee", type: "uint24" },
      { name: "lpFee", type: "uint24" },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "getLiquidity",
    inputs: [{ name: "id", type: "bytes32" }],
    outputs: [{ name: "liquidity", type: "uint128" }],
    stateMutability: "view",
  },
] as const;

// ─── BIN Pool Manager ABI (for reading pool state) ───────────────────────────
export const BIN_POOL_MANAGER_ABI = [
  {
    type: "function",
    name: "getActiveId",
    inputs: [{ name: "id", type: "bytes32" }],
    outputs: [{ name: "activeId", type: "uint24" }],
    stateMutability: "view",
  },
] as const;

// ─── CL action enums ─────────────────────────────────────────────────────────
export const CL_ACTIONS = {
  MINT_POSITION: 0,
  INCREASE_LIQUIDITY: 1,
  DECREASE_LIQUIDITY: 2,
  BURN_POSITION: 3,
  COLLECT_FEES: 4,
} as const;

// ─── BIN action enums ────────────────────────────────────────────────────────
export const BIN_ACTIONS = {
  BIN_ADD_LIQUIDITY: 0,
  BIN_REMOVE_LIQUIDITY: 1,
} as const;

// ─── Fee tiers ───────────────────────────────────────────────────────────────
export const CL_FEE_TIERS = [
  { fee: 100,   tickSpacing: 1,   label: "0.01%" },
  { fee: 500,   tickSpacing: 10,  label: "0.05%" },
  { fee: 2500,  tickSpacing: 50,  label: "0.25%" },
  { fee: 10000, tickSpacing: 200, label: "1%" },
] as const;

export const BIN_STEPS = [
  { binStep: 10,  label: "0.1%" },
  { binStep: 25,  label: "0.25%" },
  { binStep: 50,  label: "0.5%" },
  { binStep: 100, label: "1%" },
] as const;

// ─── Helper: build parameters bytes32 for CL pool (tickSpacing in low 24 bits)
export function clParameters(tickSpacing: number): `0x${string}` {
  return `0x${tickSpacing.toString(16).padStart(64, "0")}` as `0x${string}`;
}

// ─── Helper: build parameters bytes32 for BIN pool (binStep in low 16 bits)
export function binParameters(binStep: number): `0x${string}` {
  return `0x${binStep.toString(16).padStart(64, "0")}` as `0x${string}`;
}

// ─── Helper: sort currencies so currency0 is the lower address
export function sortCurrencies(a: Address, b: Address): [Address, Address] {
  return a.toLowerCase() < b.toLowerCase() ? [a, b] : [b, a];
}

// ─── Zero hooks address
export const NO_HOOKS: Address = "0x0000000000000000000000000000000000000000";

// ─── Tick math helpers ───────────────────────────────────────────────────────
export function priceToTick(
  price: number,
  token0Decimals: number,
  token1Decimals: number
): number {
  const adjustedPrice = price * Math.pow(10, token0Decimals - token1Decimals);
  return Math.floor(Math.log(adjustedPrice) / Math.log(1.0001));
}

export function tickToPrice(
  tick: number,
  token0Decimals: number,
  token1Decimals: number
): number {
  return Math.pow(1.0001, tick) / Math.pow(10, token0Decimals - token1Decimals);
}

// ─── Round tick to nearest valid tick spacing
export function nearestUsableTick(tick: number, tickSpacing: number): number {
  const rounded = Math.round(tick / tickSpacing) * tickSpacing;
  return Math.max(-887272, Math.min(887272, rounded));
}

// ─── Liquidity math approximation for CL (simplified, frontend-only estimation)
export function getLiquidityForAmounts(
  currentTick: number,
  tickLower: number,
  tickUpper: number,
  amount0: bigint,
  amount1: bigint
): bigint {
  if (tickLower >= tickUpper) return 0n;

  const sqrtRatioCurrentX96 = tickToSqrtRatioX96(currentTick);
  const sqrtRatioLowerX96 = tickToSqrtRatioX96(tickLower);
  const sqrtRatioUpperX96 = tickToSqrtRatioX96(tickUpper);

  let liquidity: bigint;

  if (sqrtRatioCurrentX96 <= sqrtRatioLowerX96) {
    liquidity = getLiquidityForAmount0(sqrtRatioLowerX96, sqrtRatioUpperX96, amount0);
  } else if (sqrtRatioCurrentX96 < sqrtRatioUpperX96) {
    const l0 = getLiquidityForAmount0(sqrtRatioCurrentX96, sqrtRatioUpperX96, amount0);
    const l1 = getLiquidityForAmount1(sqrtRatioLowerX96, sqrtRatioCurrentX96, amount1);
    liquidity = l0 < l1 ? l0 : l1;
  } else {
    liquidity = getLiquidityForAmount1(sqrtRatioLowerX96, sqrtRatioUpperX96, amount1);
  }

  return liquidity;
}

function tickToSqrtRatioX96(tick: number): bigint {
  // approximation: sqrtPrice = sqrt(1.0001^tick) * 2^96
  const price = Math.pow(1.0001, tick);
  const sqrtPrice = Math.sqrt(price);
  const Q96 = 2n ** 96n;
  return BigInt(Math.floor(sqrtPrice * Number(Q96)));
}

function getLiquidityForAmount0(
  sqrtRatioAX96: bigint,
  sqrtRatioBX96: bigint,
  amount0: bigint
): bigint {
  if (sqrtRatioAX96 > sqrtRatioBX96) {
    const tmp = sqrtRatioAX96;
    sqrtRatioAX96 = sqrtRatioBX96;
    sqrtRatioBX96 = tmp;
  }
  const Q96 = 2n ** 96n;
  const numerator = amount0 * sqrtRatioAX96 * sqrtRatioBX96;
  const denominator = Q96 * (sqrtRatioBX96 - sqrtRatioAX96);
  if (denominator === 0n) return 0n;
  return numerator / denominator;
}

function getLiquidityForAmount1(
  sqrtRatioAX96: bigint,
  sqrtRatioBX96: bigint,
  amount1: bigint
): bigint {
  if (sqrtRatioAX96 > sqrtRatioBX96) {
    const tmp = sqrtRatioAX96;
    sqrtRatioAX96 = sqrtRatioBX96;
    sqrtRatioBX96 = tmp;
  }
  const Q96 = 2n ** 96n;
  const denominator = sqrtRatioBX96 - sqrtRatioAX96;
  if (denominator === 0n) return 0n;
  return (amount1 * Q96) / denominator;
}
